import { action, actionOn, computed, thunk, thunkOn } from 'easy-peasy'
import debounce from 'lodash/debounce'

import { ADDONS } from 'common/utils/conciergeProduct.constants'
import request from 'common/utils/request'
import reset from 'artkive/stores/ecom/helpers/reset'
import { BOX_PRODUCT_TYPE } from 'artkive/stores/product.constants'
import { DISCOUNT_METHODS } from 'artkive/utils/promoCode'
import toNestedObject from 'artkive/utils/toNestedObject'

import BoxImg from 'images/ecom/checkout/Checout-Box-Thumbnail.jpg'

import { fetchBoxPrice as fetchPrice } from '../api/fetchPrice'
import customStorage from '../customStorageEngine'

import { DEFAULT_STATE as USER_STATE } from './user.store'

export const BOX_CHECKOUT_STEPS = {
  ORDER_DETAILS: 'processing',
  SHIPPING_AND_PAYMENT: 'payment',
  RECIPIENT_DETAILS: 'recipient',
}

export const defaultRecipientInfo = {
  recipientFirstName: '',
  recipientLastName: '',
  recipientEmail: '',
  recipientPhone: '',
  giftMessageTo: '',
  giftMessageFrom: '',
  giftMessage: '',
  address: '',
  address2: '',
  country: '',
  state: '',
  city: '',
  zipCode: '',
}

const DEFAULT_STATE = {
  addOns: [],
  information: { ...USER_STATE.user },
  payment: {},
  price: {},
  pricePayload: '{}',
  product: {
    id: 0,
    image: BoxImg,
    previewImage: BoxImg,
    msrp: 0,
    price: 0,
    title: 'Artkive Box',
    type: BOX_PRODUCT_TYPE,
    isTaxable: true,
  },
  productMeta: {
    addons: [],
  },
  disabledAddons: {},
  promoCode: null,
  processing: [],
  qty: 1,
  steps: [
    {
      alias: BOX_CHECKOUT_STEPS.ORDER_DETAILS,
      name: 'Order Details',
      index: 0,
      isActive: true,
      isComplete: false,
      isDirty: true,
      isVisible: true,
    },
    {
      alias: BOX_CHECKOUT_STEPS.RECIPIENT_DETAILS,
      name: 'Recipient Information',
      index: 1,
      isActive: false,
      isComplete: false,
      isDirty: true,
      isVisible: true,
    },
    {
      alias: BOX_CHECKOUT_STEPS.SHIPPING_AND_PAYMENT,
      name: 'Shipping and Billing',
      index: 2,
      isActive: false,
      isComplete: false,
      isDirty: false,
      isVisible: true,
    },
  ],
  shippingProtection: false,
  shippingProtectionPrice: 0,
  tax: 0,
  gift: false,
  sendGiftToRecipient: false,
  recipientInformation: defaultRecipientInfo,
}

const resetter = reset({ defaultState: DEFAULT_STATE, keys: ['promoCode'] })

const isFreeUsb = (price) => price?.promo_discount_details?.discount_method === DISCOUNT_METHODS.FREE_USB

const buildAddonSelector = (uuid, kind) => (uuid ? (addon) => addon.uuid === uuid : (addon) => addon.kind === kind)

const _setAddon = (state, { addon: { uuid, kind }, checked, quantity }) => {
  const selector = buildAddonSelector(uuid, kind)

  if (!checked) {
    state.addOns = state.addOns.filter((e) => !selector(e))
    return
  }

  let addOn = state.addOns.find(selector)
  if (!addOn) {
    const addOnDefinition = state.productMeta.addons.find(selector)
    addOn = {
      uuid: addOnDefinition.uuid,
      kind: addOnDefinition.kind,
      price: addOnDefinition.price,
      name: addOnDefinition.name,
    }
    state.addOns.push(addOn)
  }

  addOn.quantity = quantity ?? 1
}

const debounceFetchPrice = debounce((action, payload) => action(payload), 700)

export const STORE_NAME = 'boxStore'

const boxStore = {
  // state
  ...DEFAULT_STATE,

  // computed
  activeStep: computed((state) => state.steps.find((step) => !!step.isActive)),

  visibleSteps: computed((state) => state.steps.filter((step) => !!step.isVisible)),

  activeProcessing: computed((state) => state.processing.find((p) => p.default)),

  freeOrder: computed(({ price }) => (Object.keys(price).length > 0 ? price.total <= 0 : false)),

  fullName: computed(({ information }) => [information.firstName, information.lastName].filter(Boolean).join(' ')),

  hasSubscription: computed(({ addOns }) =>
    addOns.some(({ kind }) => kind === ADDONS.SUBSCRIPTION || kind === ADDONS.VIP_SUBSCRIPTION),
  ),

  getAddon: computed(
    ({ addOns }) =>
      ({ uuid, kind }) =>
        addOns.find((addon) => (uuid ? addon.uuid === uuid : addon.kind === kind)),
  ),

  freeAddons: computed((state) => ({
    [ADDONS.RETURN_USB]: isFreeUsb(state.price),
    [ADDONS.RETURN_ART]: +state.productMeta.addons.find((addon) => addon.kind === ADDONS.RETURN_ART)?.price === 0,
  })),

  returnArt: computed((state) => !!state.addOns.find((addon) => addon.kind === ADDONS.RETURN_ART)),

  returnUsb: computed((state) => !!state.addOns.find((addon) => addon.kind === ADDONS.RETURN_USB)),

  vipSubscription: computed((state) => !!state.addOns.find((addon) => addon.kind === ADDONS.VIP_SUBSCRIPTION)),

  shippingPrice: computed(
    ({ price }) => price.shipping_price + price.processing_price + price.shipping_protection_price,
  ),

  combinedInformation: computed((state) => ({
    ...state.information,
    recipientInformation: { ...state.recipientInformation },
  })),

  // actions
  setInformation: action((state, payload) => {
    state.information = { ...state.information, ...payload }
  }),

  setGift: action((state, payload) => {
    state.gift = payload
    if (!payload) {
      state.sendGiftToRecipient = false
    }
  }),

  setRecipientInformation: action((state, payload) => {
    state.recipientInformation = { ...state.recipientInformation, ...payload }
  }),

  setOrderInformation: action((state, payload) => {
    const normalizedPayload = toNestedObject(payload)
    if (normalizedPayload.recipientInformation) {
      state.recipientInformation = { ...state.recipientInformation, ...normalizedPayload.recipientInformation }
    }
    delete normalizedPayload.recipientInformation
    if (Object.keys(normalizedPayload).length > 0) {
      state.information = { ...state.information, ...normalizedPayload }
    }
  }),

  setSendGiftToRecipient: action((state, payload) => {
    state.sendGiftToRecipient = payload
  }),

  setProductMeta: action((state, payload) => {
    state.productMeta = { ...state.productMeta, ...payload }
  }),

  // TODO: check do we need this in ShippingAndPayment, Payment components
  setPayment: action((state, payload) => {
    state.payment = { ...state.payment, ...payload }
  }),

  setPricePayload: action((state, payload) => {
    state.pricePayload = JSON.stringify(payload)
  }),

  setPrice: action((state, payload) => {
    state.price = { ...state.price, ...payload }
  }),

  setProduct: action((state, payload) => {
    state.product = { ...state.product, ...payload }
  }),

  setProcessingTime: action((state, name) => {
    state.processing = state.processing.map((time) =>
      time.name === name ? { ...time, default: true } : { ...time, default: false },
    )
  }),

  setProcessing: action((state, payload) => {
    state.processing = payload
  }),

  setPromoCode: action((state, payload) => {
    state.promoCode = payload
  }),

  setQty: action((state, payload) => {
    if (payload <= 0 || payload >= 50) throw new Error('Quantity must be greater than 0 and less than 50')

    state.qty = payload
  }),

  setAddon: action(_setAddon),

  setAddonPessimistic: action((state, { checked, kind }) => {
    if (!checked) {
      state.addOns = state.addOns.filter((addon) => addon.kind !== kind)
    } else if (checked && !state.addOns.find((addon) => addon.kind === kind)) {
      if ((state.productMeta?.addons?.length || 0) > 0) {
        _setAddon(state, { addon: { kind }, checked })
      } else {
        state.addOns.push({ kind })
      }
    }
  }),

  setVipSubscription: action((state, checked) => {
    _setAddon(state, { addon: { uuid: ADDONS.VIP_SUBSCRIPTION }, checked })
  }),

  setDefaultAddons: action((state) => {
    state.productMeta.addons.forEach((addon) => {
      if (+addon.price === 0) {
        state.addOns.find(({ uuid }) => uuid === addon.uuid) || state.addOns.push(addon)
      }
    })
  }),

  fulfillAddons: action((state) => {
    state.addOns.forEach((selectedAddon, index) => {
      if (selectedAddon.uuid) return

      const addonDefinition = state.productMeta.addons.find(({ kind }) => selectedAddon.kind === kind)
      if (addonDefinition) {
        state.addOns[index] = { ...selectedAddon, ...addonDefinition }
      }
    })
  }),

  clearAddons: action((state) => {
    state.addOns = []
  }),

  setShippingProtection: action((state, checked) => {
    state.shippingProtection = checked
  }),

  setShippingProtectionPrice: action((state, payload) => {
    state.shippingProtectionPrice = payload
  }),

  setStepActive: action((state, { stepName }) => {
    const alias = stepName.toLowerCase()

    state.steps = state.steps.map((step) => ({ ...step, isActive: step.alias.toLowerCase() === alias }))
  }),

  setStep: action((state, { stepName, payload }) => {
    const alias = stepName.toLowerCase()

    state.steps = state.steps.map((step) => (step.alias.toLowerCase() === alias ? { ...step, ...payload } : step))
  }),

  removeDisabledAddon: action((state, { kind }) => {
    state.disabledAddons = { ...state.disabledAddons, [kind]: null }
  }),

  addDisabledAddon: action((state, { kind }) => {
    if (state.disabledAddons[kind]) return

    const selector = buildAddonSelector(null, kind)
    const addOnDefinition = state.productMeta.addons.find(selector)
    if (!addOnDefinition) return

    state.disabledAddons[kind] = addOnDefinition
    state.addOns = state.addOns.filter((e) => !selector(e))
  }),

  // thunks
  setBoxCheckoutAddonsUrl: thunk(async ({ setAddonPessimistic }) => {
    const params = new URLSearchParams(location.search)

    if (params.get('returnArt') === 'true') {
      await setAddonPessimistic({ checked: true, kind: ADDONS.RETURN_ART })
    }
    if (params.get('returnUsb') === 'true') {
      await setAddonPessimistic({ checked: true, kind: ADDONS.RETURN_USB })
    }
  }),

  fetchPrice: thunk(async (actions, payload) => {
    try {
      actions.setPricePayload(payload)
      const data = await fetchPrice(payload)
      actions.setPrice(data)
      return data
    } catch (error) {
      return { error }
    }
  }),

  fetchProcessing: thunk(async (actions, productId) => {
    try {
      const response = await request.get(Routing.processing_api_v2_orders_box_index(), {
        params: { concierge_product_id: productId },
      })

      actions.setProcessing(response.data.data)
    } catch (error) {
      return { error }
    }
  }),

  // listeners
  onItemsChange: thunkOn(
    (actions) => [
      actions.setInformation,
      actions.setOrderInformation,
      actions.setProcessingTime,
      actions.setProduct,
      actions.setPromoCode,
      actions.setQty,
      actions.setVipSubscription,
      actions.setShippingProtection,
      actions.setAddon,
      actions.setAddonPessimistic,
      actions.setGift,
      actions.setRecipientInformation,
      actions.setSendGiftToRecipient,
    ],
    async (actions, target, helpers) => {
      const {
        addOns,
        information,
        processing,
        promoCode,
        qty,
        shippingProtection,
        pricePayload,
        price,
        product,
        gift,
        sendGiftToRecipient,
        recipientInformation,
      } = helpers.getState()

      if (!product.id) return

      const conciergeProcessing = processing.find((p) => p.default)
      const shippingInformation = gift && sendGiftToRecipient ? recipientInformation : information
      const currentPricePayload = {
        concierge_product_id: product.id,
        add_ons: addOns,
        concierge_processing_id: conciergeProcessing?.id,
        promo_code: promoCode,
        quantity: qty,
        shipping_protection: shippingProtection,
        email: shippingInformation.email,
        shipping_state: shippingInformation.state,
        shipping_city: shippingInformation.city,
        shipping_zip: shippingInformation.zipCode,
        shipping_country: shippingInformation.country,
        billing_zip: information.billingZipCode,
      }

      const isPriceLoaded = Object.keys(price).length
      if (JSON.stringify(currentPricePayload) !== pricePayload || !isPriceLoaded) {
        debounceFetchPrice(actions.fetchPrice, currentPricePayload)
      }
    },
  ),

  onSetProductMeta: thunkOn(
    (actions) => [actions.setProductMeta],
    async (actions) => {
      actions.setDefaultAddons()
      actions.fulfillAddons()
    },
  ),

  onSetGift: thunkOn(
    (actions) => [actions.setGift],
    (actions, { payload }) => {
      ;[{ kind: ADDONS.SUBSCRIPTION }, { kind: ADDONS.VIP_SUBSCRIPTION }].forEach((addonPayload) => {
        payload ? actions.addDisabledAddon(addonPayload) : actions.removeDisabledAddon(addonPayload)
      })
    },
  ),

  onReset: actionOn(
    (actions) => [actions.__reset],
    (state) => {
      // TODO: maybe we want to reset other default empty objects as well - current reset mechanism doesn't change them
      state.disabledAddons = {}
    },
  ),

  ...resetter,
}

export const persistOptions = { deny: ['payment', 'price'], storage: customStorage }

export default boxStore
